import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AlertBanner from '../../components/common/AlertBanner/AlertBanner';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import Loading from '../../components/common/Loading/Loading';
import Icon from '../../components/common/Icon/Icon';
import Button from '../../components/common/Button/Button';
import config from '../../config/environment';
import { SharedTokenService } from '../../services/SharedTokenService';
import { ChargeCheckoutService } from '../../services/ChargeCheckoutService';
import { CHECKOUT_SUCCESS_DATA_QUERY_PARAM, CHECKOUT_PROCESSING_ERROR_MESSAGE, CHECKOUT_STRIPE_PREPARING_MESSAGE, createInitialCheckoutForm } from './checkout.constants';
import { getCheckoutRoutePaths } from '../../config/hostApps';
import { buildCheckoutSuccessTokenPayload } from './checkoutSuccess.utils';
import {
  parseCheckoutPlanHashFromUrl,
  parseCheckoutActionHashFromUrl,
  parseCheckoutChargeTokenFromUrl,
  parseCheckoutRefTokenFromUrl,
  resolveCheckoutPlan,
  isCheckoutTrackingRefError,
} from './checkoutPlan';
import {
  clearCheckoutIdempotencyKey,
  defaultCheckoutPaymentProvider,
} from './checkoutPaymentProvider';
import {
  applyCheckoutDraftToForm,
  buildCheckoutDraftSnapshot,
  clearCheckoutDraft,
  loadCheckoutDraft,
  saveCheckoutDraft,
} from './checkoutDraft.utils';
import type {
  CheckoutFormState,
  CheckoutSelectedPlan,
} from './checkout.types';
import type { CheckoutAvailabilityField } from './checkout.utils';
import {
  applyCheckoutPrefillToForm,
  buildCheckoutSubmissionPayload,
  buildChargeCheckoutSubmissionPayload,
  buildCheckoutAvailabilityValue,
  checkoutFormHasPayerErrors,
  copyCheckoutPayerFromStudent,
  getCheckoutAvailabilityFieldError,
  getFirstCheckoutFormError,
  isBrazilCheckoutCountry,
  resolveCheckoutPaymentProvider,
  resolveCheckoutAddressFieldError,
  resolveCheckoutPersonalFieldError,
  toPublicCheckoutErrorMessage,
  validateCardData,
  validateChargeCheckoutForm,
  validateCheckoutForm,
  validatePayerData,
} from './checkout.utils';
import { CheckoutAvailabilityService } from '../../services/CheckoutAvailabilityService';
import { withRequestIdHeaders } from '../../utils/requestId';
import CheckoutContainer from './components/CheckoutContainer';
import CheckoutHeader from './components/CheckoutHeader';
import PaymentSummaryCard from './components/PaymentSummaryCard';
import pageStyles from './CheckoutPage.module.css';
import fupStyles from '../Fup/Fup.module.css';
import DecoratedHeader from '../../components/common/DecoratedHeader/DecoratedHeader';
import Logo from '../../components/common/Logo/Logo';
import AddressStep from './steps/AddressStep';
import PaymentStep from './steps/PaymentStep';
import PersonalDataStep from './steps/PersonalDataStep';

type FeedbackState = {
  message: string;
  variant: 'error' | 'success' | 'warning';
} | null;

const checkoutPaths = getCheckoutRoutePaths();
const CHECKOUT_SUCCESS_PATH = checkoutPaths.success;
const CHECKOUT_AWAITING_PAYMENT_NAV_PATH = checkoutPaths.awaitingPayment;
const CHECKOUT_TITLE_GREETING = 'Seu próximo nível começa com uma decisão. Você está a um passo dele.';
const CHECKOUT_INFO_GREETING = 'Conclua seu checkout para darmos início à sua jornada de evolução nos treinos.';

const renderCheckoutHeader = (isCharge: boolean) => {
  if (isCharge) {
    //return <CheckoutHeader title="Pagamento" />;
  }

  return (
    <header className={pageStyles.header}>
      <Logo className={pageStyles.logoCentered} />
      <DecoratedHeader className={pageStyles.headerTitle} title={CHECKOUT_TITLE_GREETING} />
      <p className={pageStyles.headerSubtitle}>{CHECKOUT_INFO_GREETING}</p>
    </header>
  );
};

const scrollToPageTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const formatGreetingName = (fullName: string): string => {
  const trimmed = fullName.trim();

  if (!trimmed) {
    return 'aluna';
  }

  const [firstName] = trimmed.split(/\s+/);
  const normalized = firstName.toLowerCase();

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const CHECKOUT_FIELD_FOCUS_TARGETS: Record<string, string> = {
  email: 'checkout-email',
  fullName: 'checkout-full-name',
  birthDate: 'checkout-birth-date',
  cpf: 'checkout-cpf',
  phone: 'checkout-phone',
  country: 'checkout-country',
  cep: 'checkout-cep',
  state: 'checkout-state',
  city: 'checkout-city',
  neighborhood: 'checkout-neighborhood',
  street: 'checkout-street',
  number: 'checkout-number',
  complement: 'checkout-complement',
  paymentMethod: 'checkout-payment-method',
  cardHolderName: 'checkout-card-holder-name',
  cardNumber: 'checkout-card-number',
  cardExpiry: 'checkout-card-expiry',
  cardCvv: 'checkout-card-cvv',
  payerName: 'checkout-payer-name',
  payerEmail: 'checkout-payer-email',
  payerCpf: 'checkout-payer-cpf',
  payerPostalCode: 'checkout-payer-postal-code',
  payerAddressNumber: 'checkout-payer-address-number',
  payerPhone: 'checkout-payer-phone',
};

const focusFirstInvalidCheckoutField = (formErrors: Record<string, string>, delayMs = 0) => {
  const firstError = getFirstCheckoutFormError(formErrors);
  if (!firstError) {
    scrollToPageTop();
    return;
  }

  window.setTimeout(() => {
    const targetId = CHECKOUT_FIELD_FOCUS_TARGETS[firstError.field];
    const element = targetId ? document.getElementById(targetId) : null;
    const focusable = element?.matches('input, button, textarea, select, [tabindex]')
      ? element
      : element?.querySelector<HTMLElement>('input, button, textarea, select, [tabindex]');

    if (!element && !focusable) {
      scrollToPageTop();
      return;
    }

    focusable?.focus({ preventScroll: true });
    (element ?? focusable)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, delayMs);
};

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planHashFromUrl = parseCheckoutPlanHashFromUrl(searchParams);
  const actionHashFromUrl = parseCheckoutActionHashFromUrl(searchParams);
  const chargeTokenFromUrl = parseCheckoutChargeTokenFromUrl(searchParams);
  const trackingRefFromUrl = parseCheckoutRefTokenFromUrl(searchParams);
  const stripeSessionIdFromUrl = searchParams.get('stripe_session_id')?.trim() || null;
  const isChargeCheckout = Boolean(chargeTokenFromUrl);
  const initialDraft = !actionHashFromUrl && !isChargeCheckout && planHashFromUrl
    ? loadCheckoutDraft(planHashFromUrl, trackingRefFromUrl)
    : null;
  const checkoutTrackingRefToken = isChargeCheckout
    ? null
    : trackingRefFromUrl ?? initialDraft?.trackingRefToken ?? null;

  const [form, setForm] = useState<CheckoutFormState>(() => (
    initialDraft
      ? applyCheckoutDraftToForm(initialDraft, createInitialCheckoutForm())
      : createInitialCheckoutForm()
  ));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPaymentButtonLoading, setIsPaymentButtonLoading] = useState(false);
  const [isStripeConfirming, setIsStripeConfirming] = useState(false);
  const [stripeCheckout, setStripeCheckout] = useState<{
    sessionId: string;
    clientSecret: string;
  } | null>(null);
  const [readyStripeSecret, setReadyStripeSecret] = useState<string | null>(null);
  const [loadingCep, setLoadingCep] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState<CheckoutSelectedPlan | null>(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [planError, setPlanError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [chargeStudentName, setChargeStudentName] = useState('');
  const [isCheckoutPrefillReadOnly, setIsCheckoutPrefillReadOnly] = useState(() => (
    Boolean(initialDraft?.updateActionToken && initialDraft?.updateStudentId)
  ));
  const [checkoutUpdateActionToken, setCheckoutUpdateActionToken] = useState<string | null>(
    () => initialDraft?.updateActionToken ?? null,
  );
  const [checkoutUpdateStudentId, setCheckoutUpdateStudentId] = useState<number | null>(
    () => initialDraft?.updateStudentId ?? null,
  );
  const lastLookedUpCep = useRef('');
  const formRef = useRef(form);
  const stripeStatusSessionRef = useRef<string | null>(null);
  const availabilityRequestIds = useRef<Record<CheckoutAvailabilityField, number>>({
    email: 0,
    cpf: 0,
    phone: 0,
  });
  const draftReadyRef = useRef(Boolean(initialDraft) || !planHashFromUrl);

  const isInternationalCheckout = !isChargeCheckout
    && resolveCheckoutPaymentProvider(form.address.country) === 'stripe';
  const stripeClientSecret = stripeCheckout?.clientSecret ?? null;
  const stripePaymentReady = Boolean(stripeClientSecret) && readyStripeSecret === stripeClientSecret;
  const isPreparingStripePayment = isInternationalCheckout && (
    isSubmitting || Boolean(stripeClientSecret && !stripePaymentReady)
  );

  const handleStripePaymentReady = useCallback(() => {
    setReadyStripeSecret(stripeClientSecret);
  }, [stripeClientSecret]);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  useEffect(() => {
    if (!isInternationalCheckout || form.paymentMethod === 'credit_card') {
      return;
    }

    setForm((current) => ({
      ...current,
      paymentMethod: 'credit_card',
    }));
    setErrors((current) => ({ ...current, paymentMethod: '' }));
  }, [form.paymentMethod, isInternationalCheckout]);

  useEffect(() => {
    let cancelled = false;

    const loadPlan = async () => {
      setPlanLoading(true);
      setPlanError(null);
      setCheckoutPlan(null);
      setChargeStudentName('');

      if (!planHashFromUrl) {
        setPlanLoading(false);
        return;
      }

      try {
        if (chargeTokenFromUrl) {
          const preview = await ChargeCheckoutService.preview(chargeTokenFromUrl, planHashFromUrl);
          if (!cancelled) {
            setCheckoutPlan(preview.selectedPlan);
            setChargeStudentName(preview.studentName.trim());
          }
          return;
        }

        const plan = await resolveCheckoutPlan(planHashFromUrl);
        if (!cancelled) {
          setCheckoutPlan(plan);
        }
      } catch (error: unknown) {
        if (!cancelled) {
          const message = error instanceof Error && error.message.trim()
            ? error.message.trim()
            : 'Não foi possível carregar o plano informado.';

          if (!isCheckoutTrackingRefError(message)) {
            setPlanError(message);
          }
        }
      } finally {
        if (!cancelled) {
          setPlanLoading(false);
        }
      }
    };

    void loadPlan();

    return () => {
      cancelled = true;
    };
  }, [chargeTokenFromUrl, planHashFromUrl]);

  useEffect(() => {
    let cancelled = false;

    const loadCheckoutAction = async () => {
      setIsCheckoutPrefillReadOnly(false);
      setActionLoading(false);
      setActionError(null);

      if (chargeTokenFromUrl || !actionHashFromUrl) {
        draftReadyRef.current = true;
        return;
      }

      setCheckoutUpdateActionToken(null);
      setCheckoutUpdateStudentId(null);
      setActionLoading(true);

      try {
        const decoded = await SharedTokenService.decodeCheckoutStudentUpdate(actionHashFromUrl);

        if (cancelled) {
          return;
        }

        if (!decoded.checkoutPrefill || !decoded.studentId) {
          setActionError('Link de checkout expirado.');
          return;
        }

        const prefill = applyCheckoutPrefillToForm(decoded.checkoutPrefill);
        setForm((prev) => ({
          ...prev,
          personal: prefill.personal,
          address: prefill.address,
        }));
        setIsCheckoutPrefillReadOnly(true);
        setCheckoutUpdateActionToken(actionHashFromUrl);
        setCheckoutUpdateStudentId(decoded.studentId);
      } catch (error: unknown) {
        if (!cancelled) {
          setActionError(
            error instanceof Error && error.message === 'Checkout já concluído.'
              ? error.message
              : 'Link de checkout expirado.',
          );
        }
      } finally {
        if (!cancelled) {
          draftReadyRef.current = true;
          setActionLoading(false);
        }
      }
    };

    void loadCheckoutAction();

    return () => {
      cancelled = true;
    };
  }, [actionHashFromUrl, chargeTokenFromUrl]);

  useEffect(() => {
    if (isChargeCheckout || !planHashFromUrl || !draftReadyRef.current || planLoading || actionLoading) {
      return;
    }

    saveCheckoutDraft(
      planHashFromUrl,
      buildCheckoutDraftSnapshot(form, {
        updateActionToken: checkoutUpdateActionToken,
        updateStudentId: checkoutUpdateStudentId,
        trackingRefToken: checkoutTrackingRefToken,
      }),
      checkoutTrackingRefToken,
    );
  }, [
    actionLoading,
    checkoutUpdateActionToken,
    checkoutUpdateStudentId,
    checkoutTrackingRefToken,
    form,
    isChargeCheckout,
    planHashFromUrl,
    planLoading,
  ]);

  useEffect(() => {
    if (
      !stripeSessionIdFromUrl
      || !checkoutPlan
      || planLoading
      || actionLoading
      || stripeStatusSessionRef.current === stripeSessionIdFromUrl
    ) {
      return;
    }

    stripeStatusSessionRef.current = stripeSessionIdFromUrl;
    let cancelled = false;
    let pollTimer: number | null = null;
    let pollAttempts = 0;

    const checkStatus = async () => {
      pollAttempts += 1;
      const status = await defaultCheckoutPaymentProvider.getStripeCheckoutStatus(
        stripeSessionIdFromUrl,
      );

      if (cancelled) {
        return;
      }

      if (!status.success) {
        setFeedback({ message: toPublicCheckoutErrorMessage(status.message), variant: 'error' });
        stripeStatusSessionRef.current = null;
        return;
      }

      if (status.paymentResult) {
        const paidForm: CheckoutFormState = {
          ...formRef.current,
          paymentMethod: 'credit_card',
        };
        clearCheckoutIdempotencyKey();
        if (planHashFromUrl) {
          clearCheckoutDraft(planHashFromUrl, checkoutTrackingRefToken);
        }
        const tokenPayload = buildCheckoutSuccessTokenPayload(
          paidForm,
          checkoutPlan,
          status.paymentResult,
        );
        const encoded = await SharedTokenService.encodeCheckoutSuccess(tokenPayload);
        if (!cancelled) {
          navigate(
            `${CHECKOUT_SUCCESS_PATH}?${CHECKOUT_SUCCESS_DATA_QUERY_PARAM}=${encodeURIComponent(encoded.token)}`,
            { replace: true },
          );
        }
        return;
      }

      if (status.sessionStatus === 'expired') {
        clearCheckoutIdempotencyKey();
        setStripeCheckout(null);
        setFeedback({
          message: 'A sessão de pagamento Stripe expirou. Inicie uma nova tentativa.',
          variant: 'error',
        });
        return;
      }

      setFeedback({ message: status.message, variant: 'warning' });
      if (pollAttempts >= 90) {
        return;
      }
      pollTimer = window.setTimeout(() => {
        void checkStatus();
      }, 2000);
    };

    void checkStatus();

    return () => {
      cancelled = true;
      if (pollTimer !== null) {
        window.clearTimeout(pollTimer);
      }
    };
  }, [
    actionLoading,
    checkoutPlan,
    checkoutTrackingRefToken,
    navigate,
    planHashFromUrl,
    planLoading,
    stripeSessionIdFromUrl,
  ]);

  const updatePersonalField = useCallback(<K extends keyof CheckoutFormState['personal']>(
    field: K,
    value: CheckoutFormState['personal'][K],
  ) => {
    setForm((prev) => {
      const nextPersonal = { ...prev.personal, [field]: value };

      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: resolveCheckoutPersonalFieldError(field, nextPersonal, prev.address.country),
      }));

      return {
        ...prev,
        personal: nextPersonal,
      };
    });
    setFeedback(null);
  }, []);

  const handlePersonalFieldBlur = useCallback(async (field: CheckoutAvailabilityField) => {
    const personalData = form.personal;
    const addressCountry = form.address.country;

    if (field === 'cpf' && !isBrazilCheckoutCountry(addressCountry)) {
      setErrors((prev) => ({ ...prev, cpf: '' }));
      return;
    }

    const formatError = getCheckoutAvailabilityFieldError(field, personalData, addressCountry);

    if (formatError) {
      setErrors((prev) => ({ ...prev, [field]: formatError }));
      return;
    }

    const availabilityValue = buildCheckoutAvailabilityValue(field, personalData, addressCountry);
    if (!availabilityValue) {
      return;
    }

    const requestId = availabilityRequestIds.current[field] + 1;
    availabilityRequestIds.current[field] = requestId;

    try {
      const result = await CheckoutAvailabilityService.checkField(
        field,
        availabilityValue.value,
        availabilityValue.callingCode,
        checkoutUpdateStudentId ?? undefined,
        { allowCheckoutContinuation: true },
      );

      if (availabilityRequestIds.current[field] !== requestId) {
        return;
      }

      setErrors((prev) => ({
        ...prev,
        [field]: result.available ? '' : (result.message ?? 'Dado já cadastrado.'),
      }));
    } catch {
      if (availabilityRequestIds.current[field] !== requestId) {
        return;
      }
    }
  }, [checkoutUpdateStudentId, form.personal, form.address.country]);

  const updateAddressField = useCallback(<K extends keyof CheckoutFormState['address']>(
    field: K,
    value: CheckoutFormState['address'][K],
  ) => {
    if (field === 'country') {
      setStripeCheckout(null);
      clearCheckoutIdempotencyKey();
    }

    setForm((prev) => {
      const nextAddress = { ...prev.address, [field]: value };
      const nextProvider = resolveCheckoutPaymentProvider(nextAddress.country);
      const previousProvider = resolveCheckoutPaymentProvider(prev.address.country);
      const nextPaymentMethod = field === 'country' && nextProvider !== previousProvider
        ? (nextProvider === 'stripe' ? 'credit_card' : null)
        : prev.paymentMethod;

      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: resolveCheckoutAddressFieldError(field, nextAddress),
        cpf: resolveCheckoutPersonalFieldError('cpf', prev.personal, nextAddress.country),
      }));

      return {
        ...prev,
        address: nextAddress,
        paymentMethod: nextPaymentMethod,
      };
    });
    setFeedback(null);
  }, []);

  const updateCardField = useCallback(<K extends keyof CheckoutFormState['card']>(
    field: K,
    value: CheckoutFormState['card'][K],
  ) => {
    setForm((prev) => {
      const nextCard = { ...prev.card, [field]: value };
      const cardFieldErrors = validateCardData(nextCard);

      setErrors((prevErrors) => ({
        ...prevErrors,
        cardHolderName: cardFieldErrors.cardHolderName ?? '',
        cardNumber: cardFieldErrors.cardNumber ?? '',
        cardExpiry: cardFieldErrors.cardExpiry ?? '',
        cardCvv: cardFieldErrors.cardCvv ?? '',
      }));

      return {
        ...prev,
        card: nextCard,
      };
    });
    setFeedback(null);
  }, []);

  const updatePayerField = useCallback(<K extends keyof CheckoutFormState['payer']>(
    field: K,
    value: CheckoutFormState['payer'][K],
  ) => {
    setForm((prev) => {
      const nextPayer = { ...prev.payer, [field]: value };
      const payerErrors = validatePayerData(nextPayer);

      setErrors((prevErrors) => ({
        ...prevErrors,
        payerName: payerErrors.payerName ?? '',
        payerEmail: payerErrors.payerEmail ?? '',
        payerCpf: payerErrors.payerCpf ?? '',
        payerPostalCode: payerErrors.payerPostalCode ?? '',
        payerAddressNumber: payerErrors.payerAddressNumber ?? '',
        payerAddressComplement: payerErrors.payerAddressComplement ?? '',
        payerCallingCode: payerErrors.payerCallingCode ?? '',
        payerPhone: payerErrors.payerPhone ?? '',
      }));

      return {
        ...prev,
        payer: nextPayer,
      };
    });
    setFeedback(null);
  }, []);

  const updateUseStudentDataAsPayer = useCallback((useStudentData: boolean) => {
    setForm((prev) => {
      const nextPayer = useStudentData
        ? copyCheckoutPayerFromStudent(prev.personal, prev.address)
        : {
          name: '',
          email: '',
          cpf: '',
          postalCode: '',
          addressNumber: '',
          addressComplement: '',
          callingCode: '55',
          phone: '',
        };

      setErrors((prevErrors) => ({
        ...prevErrors,
        payerName: '',
        payerEmail: '',
        payerCpf: '',
        payerPostalCode: '',
        payerAddressNumber: '',
        payerAddressComplement: '',
        payerCallingCode: '',
        payerPhone: '',
      }));

      return {
        ...prev,
        useStudentDataAsPayer: useStudentData,
        payer: nextPayer,
      };
    });
    setFeedback(null);
  }, []);

  const showCheckoutValidationErrors = useCallback((formErrors: Record<string, string>) => {
    const revealPayerFields = checkoutFormHasPayerErrors(formErrors);

    if (revealPayerFields) {
      setForm((prev) => {
        if (!prev.useStudentDataAsPayer) {
          return prev;
        }

        return {
          ...prev,
          useStudentDataAsPayer: false,
          payer: copyCheckoutPayerFromStudent(prev.personal, prev.address),
        };
      });
    }

    setErrors(formErrors);
    setFeedback(null);
    focusFirstInvalidCheckoutField(formErrors, revealPayerFields ? 80 : 0);
  }, []);

  const lookupCep = useCallback(async () => {
    const cep = form.address.cep.replace(/\s/g, '');
    if (!cep || cep.length < 3) {
      return;
    }

    const countryCode = form.address.country || 'BR';
    if (countryCode === 'BR' && cep.replace(/\D/g, '').length !== 8) {
      return;
    }

    const key = `${countryCode}:${cep}`;
    if (lastLookedUpCep.current === key) {
      return;
    }

    lastLookedUpCep.current = key;
    setLoadingCep(true);

    try {
      const response = await fetch(`${config.supabaseFunctionsUrl}/address-lookup`, {
        method: 'POST',
        headers: withRequestIdHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          zipCode: cep,
          countryCode,
        }),
      });
      const data = await response.json();

      if (!response.ok || data.error) {
        setErrors((prev) => ({ ...prev, cep: data?.error || 'CEP não encontrado.' }));
        return;
      }

      setErrors((prev) => ({ ...prev, cep: '' }));
      setForm((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          street: data.street || prev.address.street,
          complement: data.complement || prev.address.complement,
          neighborhood: data.neighborhood || prev.address.neighborhood,
          city: data.city || prev.address.city,
          state: data.state || prev.address.state,
        },
      }));
    } catch {
      lastLookedUpCep.current = '';
      setErrors((prev) => ({ ...prev, cep: 'Erro ao buscar CEP.' }));
    } finally {
      setLoadingCep(false);
    }
  }, [form.address.cep, form.address.country]);

  const handleSubmit = async () => {
    if (isPaymentButtonLoading || isSubmitting || isStripeConfirming) {
      return;
    }

    const selectedPlan = checkoutPlan ?? (
      !isChargeCheckout && planHashFromUrl
        ? { hash: planHashFromUrl } as CheckoutSelectedPlan
        : null
    );

    if (!selectedPlan) {
      setFeedback({
        message: planError ?? 'Plano inválido ou não informado na URL.',
        variant: 'error',
      });
      scrollToPageTop();
      return;
    }

    if (actionError) {
      setFeedback({
        message: actionError,
        variant: 'error',
      });
      scrollToPageTop();
      return;
    }

    if (isChargeCheckout) {
      const formErrors = validateChargeCheckoutForm(form);

      if (Object.keys(formErrors).length > 0) {
        showCheckoutValidationErrors(formErrors);
        return;
      }

      if (!form.paymentMethod || !chargeTokenFromUrl) {
        if (!form.paymentMethod) {
          focusFirstInvalidCheckoutField({ paymentMethod: 'Selecione uma forma de pagamento.' });
        }
        return;
      }

      setIsSubmitting(true);

      try {
        const payload = buildChargeCheckoutSubmissionPayload(form, selectedPlan, chargeTokenFromUrl);
        const result = await ChargeCheckoutService.processCharge(payload);

        if (!result.success) {
          setFeedback({
            message: result.message || CHECKOUT_PROCESSING_ERROR_MESSAGE,
            variant: 'error',
          });
          scrollToPageTop();
          return;
        }

        const successForm: CheckoutFormState = {
          ...form,
          personal: {
            ...form.personal,
            fullName: chargeStudentName || form.payer.name || form.personal.fullName,
            email: form.payer.email || form.personal.email,
          },
        };

        const shouldAwaitPixPayment = (
          result.paymentInstructions?.method === 'pix'
          && Boolean(result.paymentInstructions.externalPaymentId)
        );

        if (shouldAwaitPixPayment) {
          const tokenPayload = buildCheckoutSuccessTokenPayload(successForm, selectedPlan, result, {
            mode: 'charge_checkout',
          });
          const encoded = await SharedTokenService.encodeCheckoutAwaitingPayment(tokenPayload);

          navigate(
            `${CHECKOUT_AWAITING_PAYMENT_NAV_PATH}?${CHECKOUT_SUCCESS_DATA_QUERY_PARAM}=${encodeURIComponent(encoded.token)}`,
            { replace: true },
          );
          setFeedback(null);
          return;
        }

        const tokenPayload = buildCheckoutSuccessTokenPayload(successForm, selectedPlan, result, {
          mode: 'charge_checkout',
        });
        const encoded = await SharedTokenService.encodeCheckoutSuccess(tokenPayload);

        navigate(
          `${CHECKOUT_SUCCESS_PATH}?${CHECKOUT_SUCCESS_DATA_QUERY_PARAM}=${encodeURIComponent(encoded.token)}`,
          { replace: true },
        );
        setFeedback(null);
      } catch {
        setFeedback({
          message: CHECKOUT_PROCESSING_ERROR_MESSAGE,
          variant: 'error',
        });
        scrollToPageTop();
      } finally {
        setIsSubmitting(false);
      }

      return;
    }

    const formErrors = validateCheckoutForm(form);
    const availabilityFields: CheckoutAvailabilityField[] = isBrazilCheckoutCountry(form.address.country)
      ? ['email', 'cpf', 'phone']
      : ['email', 'phone'];

    setIsPaymentButtonLoading(true);

    try {
    await Promise.all(availabilityFields.map(async (field) => {
      if (formErrors[field]) {
        return;
      }

      const availabilityValue = buildCheckoutAvailabilityValue(field, form.personal, form.address.country);
      if (!availabilityValue) {
        return;
      }

      try {
        const result = await CheckoutAvailabilityService.checkField(
          field,
          availabilityValue.value,
          availabilityValue.callingCode,
          checkoutUpdateStudentId ?? undefined,
          { allowCheckoutContinuation: true },
        );

        if (!result.available && result.message) {
          formErrors[field] = result.message;
        }
      } catch {
        /* availability check is best-effort on submit */
      }
    }));

    if (Object.keys(formErrors).length > 0) {
      showCheckoutValidationErrors(formErrors);
      return;
    }

    if (!form.paymentMethod) {
      focusFirstInvalidCheckoutField({ paymentMethod: 'Selecione uma forma de pagamento.' });
      return;
    }

    if (isInternationalCheckout && !config.stripePublishableKey) {
      setFeedback({
        message: 'O pagamento internacional está temporariamente indisponível.',
        variant: 'error',
      });
      scrollToPageTop();
      return;
    }

    setIsSubmitting(true);

    try {
      const stripeReturnUrl = new URL(window.location.href);
      stripeReturnUrl.searchParams.delete('stripe_session_id');
      const payload = buildCheckoutSubmissionPayload(form, selectedPlan, {
        updateActionToken: checkoutUpdateActionToken,
        trackingRefToken: checkoutTrackingRefToken,
        stripeReturnUrl: isInternationalCheckout ? stripeReturnUrl.toString() : null,
      });
      const result = await defaultCheckoutPaymentProvider.processCheckout(payload);

      if (!result.success) {
        const nextUpdateToken = result.continuation?.updateActionToken ?? checkoutUpdateActionToken;
        const nextStudentId = result.continuation?.studentId ?? checkoutUpdateStudentId;

        if (result.continuation) {
          setCheckoutUpdateActionToken(result.continuation.updateActionToken);
          setCheckoutUpdateStudentId(result.continuation.studentId);
        }

        if (planHashFromUrl) {
          saveCheckoutDraft(
            planHashFromUrl,
            buildCheckoutDraftSnapshot(form, {
              updateActionToken: nextUpdateToken,
              updateStudentId: nextStudentId,
              trackingRefToken: checkoutTrackingRefToken,
            }),
            checkoutTrackingRefToken,
          );
        }

        setFeedback({
          message: result.message || CHECKOUT_PROCESSING_ERROR_MESSAGE,
          variant: 'error',
        });
        scrollToPageTop();
        return;
      }

      if (result.stripeCheckout) {
        setStripeCheckout(result.stripeCheckout);
        setFeedback(null);
        return;
      }

      if (planHashFromUrl) {
        clearCheckoutDraft(planHashFromUrl, checkoutTrackingRefToken);
      }

      const shouldAwaitPixPayment = (
        result.paymentInstructions?.method === 'pix'
        && Boolean(result.paymentInstructions.externalPaymentId)
      );

      if (shouldAwaitPixPayment) {
        const tokenPayload = buildCheckoutSuccessTokenPayload(form, selectedPlan, result);
        const encoded = await SharedTokenService.encodeCheckoutAwaitingPayment(tokenPayload);

        navigate(
          `${CHECKOUT_AWAITING_PAYMENT_NAV_PATH}?${CHECKOUT_SUCCESS_DATA_QUERY_PARAM}=${encodeURIComponent(encoded.token)}`,
          { replace: true },
        );
        setFeedback(null);
        return;
      }

      const tokenPayload = buildCheckoutSuccessTokenPayload(form, selectedPlan, result);
      const encoded = await SharedTokenService.encodeCheckoutSuccess(tokenPayload);

      navigate(
        `${CHECKOUT_SUCCESS_PATH}?${CHECKOUT_SUCCESS_DATA_QUERY_PARAM}=${encodeURIComponent(encoded.token)}`,
        { replace: true },
      );
      setFeedback(null);
    } catch {
      setFeedback({
        message: CHECKOUT_PROCESSING_ERROR_MESSAGE,
        variant: 'error',
      });
      scrollToPageTop();
    } finally {
      setIsSubmitting(false);
    }
    } finally {
      setIsPaymentButtonLoading(false);
    }
  };

  if (planLoading || actionLoading) {
    return (
      <CheckoutContainer>
        {renderCheckoutHeader(isChargeCheckout)}
        <Loading />
      </CheckoutContainer>
    );
  }

  if (!planHashFromUrl) {
    return (
      <CheckoutContainer>
        {renderCheckoutHeader(isChargeCheckout)}
        <EmptyState message="Esta página de checkout é inválida. Verifique o link recebido." />
      </CheckoutContainer>
    );
  }

  if (actionError) {
    return (
      <CheckoutContainer>
        {renderCheckoutHeader(isChargeCheckout)}
        <EmptyState message={actionError} />
      </CheckoutContainer>
    );
  }

  if (isChargeCheckout && planError) {
    return (
      <CheckoutContainer>
        {renderCheckoutHeader(true)}
        <EmptyState message={planError} />
      </CheckoutContainer>
    );
  }

  return (
    <CheckoutContainer>
      {renderCheckoutHeader(isChargeCheckout)}

      {planError && !isChargeCheckout && (
        <AlertBanner message={planError} variant="error" />
      )}

      {feedback && (
        <AlertBanner
          message={feedback.message}
          variant={feedback.variant}
        />
      )}

      <div className={`${pageStyles.layout} ${isChargeCheckout ? pageStyles.layoutCharge : ''}`.trim()}>
        {!isChargeCheckout && (
          <article className={pageStyles.checkoutCard}>
            <h2 className={pageStyles.cardTitle}>Dados pessoais</h2>
            <PersonalDataStep
              data={form.personal}
              errors={errors}
              requiresCpf={isBrazilCheckoutCountry(form.address.country)}
              disabled={isSubmitting || isStripeConfirming || Boolean(stripeCheckout)}
              readOnlyFields={isCheckoutPrefillReadOnly ? {
                fullName: form.personal.fullName,
                email: form.personal.email,
                callingCode: form.personal.callingCode,
                phone: form.personal.phone,
              } : undefined}
              onFieldChange={updatePersonalField}
              onFieldBlur={(field) => {
                void handlePersonalFieldBlur(field);
              }}
            />

            <h2 className={pageStyles.cardTitle}>Endereço</h2>
            <AddressStep
              data={form.address}
              errors={errors}
              loadingCep={loadingCep}
              disabled={isSubmitting || isStripeConfirming || Boolean(stripeCheckout)}
              onFieldChange={updateAddressField}
              onCepBlur={() => { void lookupCep(); }}
            />
          </article>
        )}

        <article
          className={`${pageStyles.checkoutCard} ${isPreparingStripePayment ? pageStyles.checkoutCardBusy : ''}`.trim()}
          aria-busy={isPreparingStripePayment}
        >
          <div
            className={`${pageStyles.checkoutCardContent} ${isPreparingStripePayment ? pageStyles.checkoutCardBlurred : ''}`.trim()}
          >
          <h2 className={pageStyles.cardTitle}>Forma de pagamento</h2>
          <PaymentStep
            paymentMethod={form.paymentMethod}
            card={form.card}
            payer={form.payer}
            useStudentDataAsPayer={form.useStudentDataAsPayer}
            chargeCheckoutMode={isChargeCheckout}
            internationalCheckout={isInternationalCheckout}
            stripeClientSecret={stripeCheckout?.clientSecret ?? null}
            stripePaymentReady={stripePaymentReady}
            paymentError={errors.paymentMethod}
            cardErrors={errors}
            disabled={isSubmitting || isStripeConfirming}
            onStripeSubmittingChange={setIsStripeConfirming}
            onStripeError={(message) => {
              if (!message) {
                setFeedback(null);
                return;
              }

              setFeedback({
                message: toPublicCheckoutErrorMessage(message),
                variant: 'error',
              });
            }}
            onStripeLoadFailed={() => {
              setStripeCheckout(null);
              setReadyStripeSecret(null);
              setIsStripeConfirming(false);
              clearCheckoutIdempotencyKey();
            }}
            onStripeReady={handleStripePaymentReady}
            onStripeConfirmed={() => {
              if (!stripeCheckout?.sessionId) {
                return;
              }

              const nextUrl = new URL(window.location.href);
              nextUrl.searchParams.set('stripe_session_id', stripeCheckout.sessionId);
              navigate(`${nextUrl.pathname}${nextUrl.search}`, { replace: true });
            }}
            onPaymentMethodChange={(paymentMethod) => {
              setForm((prev) => ({
                ...prev,
                paymentMethod,
                useStudentDataAsPayer: paymentMethod === 'credit_card'
                  ? false
                  : prev.useStudentDataAsPayer,
                payer: paymentMethod === 'credit_card'
                  ? {
                    name: '',
                    email: '',
                    cpf: '',
                    postalCode: '',
                    addressNumber: '',
                    addressComplement: '',
                    callingCode: '55',
                    phone: '',
                  }
                  : prev.payer,
                card: paymentMethod === 'credit_card' && !prev.card.holderName.trim()
                  ? {
                    ...prev.card,
                    holderName: prev.personal.fullName.trim(),
                  }
                  : prev.card,
              }));
              setErrors((prev) => ({
                ...prev,
                paymentMethod: '',
                cardHolderName: '',
                cardNumber: '',
                cardExpiry: '',
                cardCvv: '',
                payerName: '',
                payerEmail: '',
                payerCpf: '',
                payerPostalCode: '',
                payerAddressNumber: '',
                payerAddressComplement: '',
                payerCallingCode: '',
                payerPhone: '',
              }));
              setFeedback(null);
            }}
            onCardFieldChange={updateCardField}
            onPayerFieldChange={updatePayerField}
            onUseStudentDataAsPayerChange={updateUseStudentDataAsPayer}
          />

          <hr className={pageStyles.cardDivider} />

          <PaymentSummaryCard
            selectedPlan={checkoutPlan}
            discountCents={form.discountCents}
            paymentMethod={form.paymentMethod}
          />

          {!stripePaymentReady && (
            <div className={fupStyles.footerActions}>
              <Button
                type="button"
                variant="primary"
                size="app"
                fullWidth
                className={isPaymentButtonLoading || isSubmitting ? pageStyles.checkoutButtonBusy : ''}
                disabled={isStripeConfirming}
                aria-busy={isPaymentButtonLoading || isSubmitting}
                onClick={() => {
                  void handleSubmit();
                }}
              >
                {isPaymentButtonLoading || isSubmitting ? (
                  <Icon
                    name="progress_activity"
                    size={28}
                    color="currentColor"
                    className={pageStyles.checkoutButtonSpinner}
                    style={{ fontVariationSettings: "'FILL' 0, 'wght' 500, 'GRAD' 0, 'opsz' 24" }}
                  />
                ) : isChargeCheckout
                  ? 'Finalizar pagamento'
                  : isInternationalCheckout
                    ? 'Continuar para pagamento'
                    : 'Finalizar contratação'}
              </Button>
            </div>
          )}
          </div>
          {isPreparingStripePayment && (
            <div className={pageStyles.checkoutCardLoading} role="status">
              <Icon
                name="progress_activity"
                size={112}
                color="var(--color-brand-primary)"
                className={pageStyles.checkoutCardLoadingSpinner}
                style={{ fontVariationSettings: "'FILL' 0, 'wght' 500, 'GRAD' 0, 'opsz' 48" }}
              />
              <p className={pageStyles.checkoutCardLoadingMessage}>
                {CHECKOUT_STRIPE_PREPARING_MESSAGE}
              </p>
            </div>
          )}
        </article>
      </div>
    </CheckoutContainer>
  );
};

export default CheckoutPage;
